import { isOk } from "@/common/common";
import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { customFieldsApiClient } from "./customFieldsApiClient";
import { CustomFieldDataType } from "./customFieldsDomain";
import {
  blankUnmappedCells,
  cellEditor,
  errorCount,
  ImportFieldOption,
  resultRows,
  SpreadsheetImportGrid,
  SpreadsheetImportIssue,
  SpreadsheetImportResult,
  SpreadsheetImportRow,
} from "./spreadsheetImportDomain";
import {
  openSpreadsheetImport,
  prepSpreadsheetImportFlow,
  setupOf,
  SpreadsheetImportFlow,
  SpreadsheetImportFlowProps,
  SpreadsheetImportState,
} from "./spreadsheetImportFlow";

type RoleImportField =
  | "PERSON_NAME"
  | "ROLE"
  | "DATE_FROM"
  | "DATE_TO"
  | "BIRTH_DATE"
  | "EMAIL"
  | "PHONE_NUMBER"
  | "NATIONAL_ID"
  | "D_NUMBER"
  | "NATIONALITY"
  | "ADDRESS_LINE_1"
  | "ADDRESS_LINE_2"
  | "POSTAL_CODE"
  | "CITY"
  | "ADDRESS_COUNTRY";

type CompanyRoleImportTarget =
  | { targetType: "SYSTEM"; field: RoleImportField }
  | { targetType: "CUSTOM"; definitionId: string };

export type CompanyRoleImportField = {
  target: CompanyRoleImportTarget;
  dataType: CustomFieldDataType;
  name?: string;
  options?: string[];
  required?: boolean;
};

export type CompanyRoleImportSheet = {
  headers: string[];
  rows: SpreadsheetImportRow[];
  fields: CompanyRoleImportField[];
  countryCodes: string[];
  fieldCountryCodes: string[];
};

export type CompanyRoleImportRequest = {
  rows: SpreadsheetImportRow[];
  columns: (CompanyRoleImportTarget | null)[];
};

export type CompanyRoleImportResponse = {
  created: number;
  updated: number;
  unchanged: number;
  issues: SpreadsheetImportIssue[];
};

export type CompanyRoleImportSetup = Omit<CompanyRoleImportSheet, "rows">;

export type CompanyRoleImportState = SpreadsheetImportState<
  CompanyRoleImportSetup,
  CompanyRoleImportResponse
>;

const roleFieldLabels: Record<RoleImportField, TMessage> = {
  PERSON_NAME: { key: "entity-data.role-import-person-name" },
  ROLE: { key: "entity-data.role-import-role" },
  DATE_FROM: { key: "entity-data.role-import-date-from" },
  DATE_TO: { key: "entity-data.role-import-date-to" },
  BIRTH_DATE: { key: "entity-data.role-import-birth-date" },
  EMAIL: { key: "email" },
  PHONE_NUMBER: { key: "settings.phoneNumber" },
  NATIONAL_ID: { key: "shareholders.editSingle.socialSecurityNumber" },
  D_NUMBER: { key: "settings.dNumber" },
  NATIONALITY: { key: "settings.nationality" },
  ADDRESS_LINE_1: { key: "entity-data.address-part-line1" },
  ADDRESS_LINE_2: { key: "entity-data.address-part-line2" },
  POSTAL_CODE: { key: "entity-data.address-part-postal-code" },
  CITY: { key: "company-roles.residential-address.city" },
  ADDRESS_COUNTRY: { key: "entity-data.address-part-country" },
};

export const roleFieldKey = (target: CompanyRoleImportTarget): string =>
  target.targetType === "SYSTEM" ? `SYSTEM:${target.field}` : `CUSTOM:${target.definitionId}`;

const addressFields: ReadonlySet<RoleImportField> = new Set([
  "ADDRESS_LINE_1",
  "ADDRESS_LINE_2",
  "POSTAL_CODE",
  "CITY",
  "ADDRESS_COUNTRY",
]);

const fieldSection = (target: CompanyRoleImportTarget): TMessage => {
  if (target.targetType === "CUSTOM") return { key: "entity-data.spreadsheet-custom-fields" };
  if (addressFields.has(target.field)) return { key: "company-roles.residential-address.title" };
  return { key: "entity-data.role-import-section" };
};

const fieldOption = (field: CompanyRoleImportField): ImportFieldOption => ({
  key: roleFieldKey(field.target),
  label:
    field.target.targetType === "SYSTEM"
      ? roleFieldLabels[field.target.field]
      : { translated: field.name ?? "" },
  section: fieldSection(field.target),
  editor: cellEditor(field.dataType, field.options),
});

const companyRoleImportFlow: SpreadsheetImportFlow<
  CompanyRoleImportSetup,
  CompanyRoleImportRequest,
  CompanyRoleImportResponse
> = {
  set: (change) => ({ type: "update-store", data: { companyRoleImport: change } }),
  stateOf: (root) => root.companyRoleImport,
  initial: {},
  requestOf: (state) => importRequest(state),
  validate: customFieldsApiClient.validateCompanyRoleImport,
  import: customFieldsApiClient.importCompanyRoles,
};

export const openCompanyRoleImport = (
  accountId: string,
  files: File[] | null,
  afterImport: DCEvent[],
): DCEvent[] =>
  openSpreadsheetImport(
    companyRoleImportFlow,
    accountId,
    files,
    afterImport,
    customFieldsApiClient.readCompanyRoleImport,
    ({ rows, ...setup }) => ({ setup, rows, columns: setup.headers.map(() => undefined) }),
  );

const noSetup: CompanyRoleImportSetup = {
  headers: [],
  fields: [],
  countryCodes: [],
  fieldCountryCodes: [],
};

export const gridOf = (state: CompanyRoleImportState): SpreadsheetImportGrid => ({
  headers: setupOf(state, noSetup).headers,
  rows: state.rows,
  columns: state.columns,
  fieldsByKey: new Map(
    setupOf(state, noSetup).fields.map((field) => [roleFieldKey(field.target), fieldOption(field)]),
  ),
  errorsOnly: state.errorsOnly,
  selectedOnly: state.selectedOnly,
});

export const missingRequired = (
  state: CompanyRoleImportState,
  grid: SpreadsheetImportGrid,
): TMessage[] =>
  setupOf(state, noSetup)
    .fields.filter((field) => field.required && !state.columns.includes(roleFieldKey(field.target)))
    .flatMap((field) => grid.fieldsByKey.get(roleFieldKey(field.target))?.label ?? []);

export const importRequest = (state: CompanyRoleImportState): CompanyRoleImportRequest => {
  const targetsByKey = new Map(
    setupOf(state, noSetup).fields.map((field) => [roleFieldKey(field.target), field.target]),
  );
  const columns = state.columns.map((key) => (key && targetsByKey.get(key)) || null);
  return {
    rows: blankUnmappedCells(state.rows, (index) => columns[index] !== null),
    columns,
  };
};

const importResultOf = (
  state: CompanyRoleImportState,
  response: CompanyRoleImportResponse,
): SpreadsheetImportResult => ({
  counts: [
    { key: "created", label: { key: "entity-data.role-import-created" }, value: response.created },
    { key: "updated", label: { key: "entity-data.role-import-updated" }, value: response.updated },
    {
      key: "unchanged",
      label: { key: "entity-data.role-import-unchanged" },
      value: response.unchanged,
    },
    {
      key: "rejected",
      label: { key: "entity-data.role-import-not-imported" },
      value: errorCount(response.issues),
    },
  ],
  nothingImported: response.created + response.updated === 0,
  rows: resultRows(gridOf(state), response.issues),
});

export type CompanyRoleImportViewProps = SpreadsheetImportFlowProps<CompanyRoleImportSetup> & {
  missingRequired: TMessage[];
  canImport: boolean;
  result?: SpreadsheetImportResult;
};

export const prepCompanyRoleImportView = (
  state: CompanyRoleImportState | undefined,
  dispatch: EventDispatcher,
): CompanyRoleImportViewProps | undefined => {
  if (!state) {
    return undefined;
  }
  const grid = gridOf(state);
  const missing = missingRequired(state, grid);
  return {
    ...prepSpreadsheetImportFlow(
      companyRoleImportFlow,
      state,
      setupOf(state, noSetup),
      grid,
      isOk(state.validation) ? state.validation.data.issues : [],
      dispatch,
    ),
    missingRequired: missing,
    canImport: isOk(state.setupResult) && state.rows.length > 0 && missing.length === 0,
    result: isOk(state.importResult) ? importResultOf(state, state.importResult.data) : undefined,
  };
};
